import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { addDays } from 'date-fns';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { toastService } from '@services/toast.service';
import { toLocalIso } from '@/utils/datetime';
import { collaborationPlanService, CollaborationStepType } from '@/services/collaboration-plan.service';
import { COLLABORATION_STEP_VISUALS } from './collaborationStepIcons';
import { withDefaultTime } from './collaborationStepSchedule';

interface CollaborationPlanPresetDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: number | string;
  onSaved: () => void;
}

// Не з каталогу/enum-порядку (TYPICAL_COLLABORATION_STEP_TYPES) - окремий, задуманий саме для цього
// вікна порядок класичного флоу співпраці (user, 2026-10-29). PROGRESS_REVIEW_REMIND свідомо
// відсутній - умовний крок (+2 дні без відповіді клієнта), не частина стартової заготовки.
const PRESET_TYPES: Exclude<CollaborationStepType, 'custom' | 'progress_review_remind'>[] = [
  'personal_questionnaire',
  'schedule_first_consultation',
  'first_meeting',
  'meeting_summary',
  'create_client_plan',
  'schedule_repeat_meeting',
  'progress_review_send',
  'progress_review_view',
  'repeat_meeting',
];

interface PresetRow {
  type: typeof PRESET_TYPES[number];
  checked: boolean;
  date: Date | null;
}

const buildInitialRows = (): PresetRow[] =>
  PRESET_TYPES.map((type, index) => ({ type, checked: true, date: addDays(new Date(), index) }));

/** "Рекомендації до плану співпраці" (кнопка "Рекомендації") (не в Figma, ідея користувача 2026-10-29) - за один раз додає серію
 *  типових задач класичного флоу співпраці. Чекбокс - чи додавати пункт (за замовч. всі), дата - без
 *  часу, за замовч. вже розставлена послідовно від сьогодні (можна скорегувати чи очистити - без
 *  дедлайну теж ок). Створення - один запит (`createStepsBatch`, бек: одна транзакція - усі задачі
 *  разом або жодна), не N окремих `createStep`. Порядок масиву = порядок списку, визначає `id`
 *  (і, отже, тай-брейк сортування плану при однакових/відсутніх датах,
 *  collaboration-plan.service.ts `getActivePlan`/`getAllSteps`). */
export const CollaborationPlanPresetDialog: React.FC<CollaborationPlanPresetDialogProps> = ({
  open, onClose, clientId, onSaved,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.collaborationPlan' });
  const [rows, setRows] = useState<PresetRow[]>(buildInitialRows);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRows(buildInitialRows());
  }, [open]);

  const toggleRow = (type: PresetRow['type']) => {
    setRows((prev) => prev.map((r) => (r.type === type ? { ...r, checked: !r.checked } : r)));
  };
  const setRowDate = (type: PresetRow['type'], date: Date | null) => {
    setRows((prev) => prev.map((r) => (r.type === type ? { ...r, date } : r)));
  };

  const checkedCount = rows.filter((r) => r.checked).length;

  const handleSave = async () => {
    const selected = rows.filter((r) => r.checked);
    if (selected.length === 0) return;
    setSaving(true);
    try {
      await collaborationPlanService.createStepsBatch(
        clientId,
        selected.map((row) => ({
          type: row.type,
          scheduledLdt: row.date ? toLocalIso(withDefaultTime(row.date)) : undefined,
        })),
      );
      toastService.success(t('preset.success', { count: selected.length }));
      onSaved();
      onClose();
    } catch {
      toastService.error(t('preset.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={t('preset.title')}
      maxWidth={false}
      paperSx={{ width: 640, borderRadius: '16px' }}
      titleSx={{ fontSize: 20, lineHeight: '23px', fontWeight: 600, color: '#26313F' }}
      contentSx={{ p: 3, borderBottom: 'none' }}
      actions={
        <Button
          onClick={handleSave}
          disabled={checkedCount === 0 || saving}
          sx={{
            bgcolor: '#6EB52C', color: '#FFFFFF', borderRadius: '6px', minWidth: 160,
            height: 40, px: 2, fontSize: 14, fontWeight: 500, textTransform: 'none',
            boxShadow: 'none', '&:hover': { bgcolor: '#6EB52C', boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#D5D7D4', color: '#FFFFFF' },
          }}
        >
          {saving ? t('preset.saving') : t('preset.submit', { count: checkedCount })}
        </Button>
      }
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
        <Typography sx={{ fontSize: 13, color: '#666666', mb: 2 }}>{t('preset.hint')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rows.map((row) => {
            const visual = COLLABORATION_STEP_VISUALS[row.type];
            return (
              <Box key={row.type} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={row.checked}
                  onChange={() => toggleRow(row.type)}
                  size="small"
                  sx={{ p: '4px', flexShrink: 0, '&.Mui-checked': { color: '#6EB52C' } }}
                />
                <Box
                  sx={{
                    width: 28, height: 28, borderRadius: '4px', bgcolor: visual.bg, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: row.checked ? 1 : 0.5,
                  }}
                >
                  <visual.Icon sx={{ fontSize: 16, color: visual.color }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 14, flexGrow: 1, minWidth: 0, color: row.checked ? '#26313F' : '#989898',
                  }}
                >
                  {t(`step.${row.type}`)}
                </Typography>
                <DatePicker
                  value={row.date}
                  onChange={(date) => setRowDate(row.type, date)}
                  format="dd.MM.yyyy"
                  disabled={!row.checked}
                  slotProps={{ textField: { size: 'small', sx: { width: 160 } }, field: { clearable: true } }}
                />
              </Box>
            );
          })}
        </Box>
      </LocalizationProvider>
    </BaseDialog>
  );
};
